import { useRef, useState } from 'react'
import { useApp, useAuth } from '../components/AppContext'
import { fmt } from '../lib/tax'
import { Camera, CheckCircle, AlertCircle, X } from 'lucide-react'

export default function Receipts() {
  const { transactions, uploadReceipt } = useApp()
  const { profile } = useAuth()
  const [selected, setSelected]       = useState(null)
  const [uploading, setUploading]     = useState(false)
  const [uploadError, setUploadError] = useState('')
  const [justUploaded, setJustUploaded] = useState(null)
  const fileRef = useRef(null)

  const isVatRegistered = profile?.vat_registered || false

  const expenses       = transactions.filter(t => t.type === 'expense')
  const withReceipt    = expenses.filter(t => t.has_vat_receipt)
  const withoutReceipt = expenses.filter(t => !t.has_vat_receipt)
  const reclaimable    = withReceipt.reduce((s,t) => s + (t.amount / 1.20) * 0.20, 0)
  const potential      = withoutReceipt.reduce((s,t) => s + (t.amount / 1.20) * 0.20, 0)
  const coverage       = expenses.length > 0 ? Math.round((withReceipt.length / expenses.length) * 100) : 0

  // THE FIX: pass the actual file object to uploadReceipt
  async function handleFile(e) {
    const file = e.target.files[0]
    if (!file || !selected) return
    e.target.value = '' // reset so same file can be re-selected
    setUploading(true)
    setUploadError('')
    try {
      await uploadReceipt(selected.id, file) // file now passed correctly
      setJustUploaded(selected.id)
      setSelected(null)
      setTimeout(() => setJustUploaded(null), 3000)
    } catch (err) {
      console.error('Receipt upload failed:', err)
      setUploadError('Upload failed — check your connection and try again.')
    } finally {
      setUploading(false)
    }
  }

  function openFilePicker(e) {
    e.stopPropagation()
    setUploadError('')
    fileRef.current?.click()
  }

  return (
    <div className="fade-up">
      <div className="page-head">
        <h1 className="page-title">Receipts{isVatRegistered ? ' & VAT' : ''}</h1>
        <p className="page-sub">
          {isVatRegistered
            ? 'Upload receipts to reclaim input VAT and evidence your expenses'
            : 'Keep receipts as proof of business expenses — HMRC can ask for them'}
        </p>
      </div>

      {/* Stats */}
      <div className={isVatRegistered ? 'g3' : 'g2'} style={{ marginBottom:20 }}>
        {isVatRegistered && (
          <>
            <div className="stat">
              <div className="stat-label">VAT Reclaimable</div>
              <div className="stat-value teal">{fmt(reclaimable)}</div>
              <div className="stat-sub">{withReceipt.length} receipts uploaded</div>
            </div>
            <div className="stat">
              <div className="stat-label">Potential VAT Missed</div>
              <div className="stat-value amber">{fmt(potential)}</div>
              <div className="stat-sub">{withoutReceipt.length} without receipt</div>
            </div>
          </>
        )}
        {!isVatRegistered && (
          <div className="stat">
            <div className="stat-label">Receipts Uploaded</div>
            <div className="stat-value teal">{withReceipt.length}</div>
            <div className="stat-sub">of {expenses.length} expenses</div>
          </div>
        )}
        <div className="stat">
          <div className="stat-label">Receipt Coverage</div>
          <div className="stat-value" style={{ color: coverage >= 70 ? 'var(--teal)' : 'var(--amber)' }}>{coverage}%</div>
          <div className="stat-sub">of expense transactions</div>
        </div>
      </div>

      {/* Info banner */}
      {isVatRegistered ? (
        <div className="alert alert-teal" style={{ marginBottom:20 }}>
          <strong>VAT rule:</strong> No receipt = no VAT reclaim. Quarterly excludes VAT automatically where no receipt is uploaded.
        </div>
      ) : (
        <div className="alert" style={{ marginBottom:20, background:'rgba(59,158,255,.06)', border:'1px solid rgba(59,158,255,.2)', color:'var(--white)' }}>
          <strong>Why keep receipts?</strong> Even if you're not VAT registered, HMRC can ask you to prove your expenses.
        </div>
      )}

      {/* Success toast */}
      {justUploaded && (
        <div className="alert alert-teal fade-in" style={{ marginBottom:16 }}>
          <CheckCircle size={14} /> Receipt saved successfully
        </div>
      )}

      {/* Error */}
      {uploadError && (
        <div className="alert alert-red fade-in" style={{ marginBottom:16 }}>
          <AlertCircle size={14} /> {uploadError}
          <button onClick={() => setUploadError('')} style={{ marginLeft:'auto', background:'none', border:'none', cursor:'pointer', color:'var(--red)', padding:0 }}>
            <X size={13} />
          </button>
        </div>
      )}

      {/* File input — capture="environment" opens rear camera on mobile by default */}
      <input
        ref={fileRef}
        type="file"
        accept="image/*,.pdf"
        capture="environment"
        style={{ display:'none' }}
        onChange={handleFile}
      />

      <div className="g2">
        {/* Needs receipt */}
        <div>
          <div style={{ fontSize:13, fontWeight:600, color:'var(--amber)', marginBottom:12, display:'flex', alignItems:'center', gap:6 }}>
            <AlertCircle size={13} /> Needs Receipt ({withoutReceipt.length})
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            {withoutReceipt.length === 0 && (
              <div style={{ padding:'24px', textAlign:'center', color:'var(--slate-dim)', fontSize:13 }}>All expenses have receipts</div>
            )}
            {withoutReceipt.map(tx => (
              <div key={tx.id} className="card card-sm"
                style={{ cursor:'pointer', border: selected?.id === tx.id ? '1px solid var(--teal)' : undefined, transition:'border-color .15s' }}
                onClick={() => setSelected(s => s?.id === tx.id ? null : tx)}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                  <div style={{ minWidth:0, flex:1 }}>
                    <div style={{ fontSize:13, fontWeight:500, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{tx.description}</div>
                    <div style={{ fontSize:11, color:'var(--slate-dim)' }}>{tx.date}</div>
                  </div>
                  <div style={{ textAlign:'right', flexShrink:0, marginLeft:12 }}>
                    <div style={{ fontSize:13.5, fontWeight:600 }}>{fmt(tx.amount)}</div>
                    <div style={{ fontSize:11, color:'var(--red)' }}>
                      {isVatRegistered ? 'No VAT receipt' : 'No receipt'}
                    </div>
                  </div>
                </div>

                {selected?.id === tx.id && (
                  <div style={{ marginTop:12 }} className="fade-in">
                    <div className={`receipt-zone ${uploading ? 'active' : ''}`} onClick={openFilePicker} style={{ minHeight:80 }}>
                      {uploading ? (
                        <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:8 }}>
                          <div className="spin" />
                          <div style={{ fontSize:12, color:'var(--teal)' }}>Uploading...</div>
                        </div>
                      ) : (
                        <>
                          <Camera size={22} color="var(--slate)" style={{ marginBottom:8 }} />
                          <div style={{ fontSize:13, color:'var(--slate)', fontWeight:500 }}>Tap to take photo or upload</div>
                          <div style={{ fontSize:11, color:'var(--slate-dim)', marginTop:3 }}>JPG, PNG or PDF</div>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Has receipt */}
        <div>
          <div style={{ fontSize:13, fontWeight:600, color:'var(--teal)', marginBottom:12, display:'flex', alignItems:'center', gap:6 }}>
            <CheckCircle size={13} /> Receipt Uploaded ({withReceipt.length})
          </div>
          {withReceipt.length === 0
            ? <div style={{ padding:'32px', textAlign:'center', color:'var(--slate-dim)', fontSize:13 }}>No receipts yet</div>
            : (
              <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                {withReceipt.map(tx => (
                  <div key={tx.id} className="card card-sm" style={{ opacity:.75 }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                      <div style={{ minWidth:0, flex:1 }}>
                        <div style={{ fontSize:13, fontWeight:500, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{tx.description}</div>
                        <div style={{ fontSize:11, color:'var(--slate-dim)' }}>{tx.date}</div>
                      </div>
                      <div style={{ textAlign:'right', flexShrink:0, marginLeft:12 }}>
                        <div style={{ fontSize:13.5, fontWeight:600 }}>{fmt(tx.amount)}</div>
                        {isVatRegistered
                          ? <div style={{ fontSize:11, color:'var(--teal)' }}>✓ VAT: {fmt((tx.amount/1.20)*0.20)}</div>
                          : <div style={{ fontSize:11, color:'var(--teal)' }}>✓ Receipt saved</div>
                        }
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )
          }
        </div>
      </div>
    </div>
  )
}
